import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faImage, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

// Read-only product review sheet for the admin approval queue.
//
// Reviewing a pending listing used to mean opening the Edit form — the only
// screen that showed the description, specs and full image set. That is the
// wrong tool for the job twice over: it's a mutation form opened for a read,
// and one stray keystroke edits a seller's listing while you're deciding
// whether to approve it. This shows everything, changes nothing, and carries
// the Approve/Reject actions so the decision happens where the evidence is.
const ProductReviewModal = ({
  product,
  categories = [],
  isDarkMode = false,
  onClose,
  onApprove,
  onReject,
}) => {
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!product) return null;

  const c = {
    surface: isDarkMode ? '#15151d' : '#ffffff',
    raised: isDarkMode ? 'rgba(255,255,255,0.035)' : '#f8fafc',
    border: isDarkMode ? 'rgba(255,255,255,0.09)' : '#e5e7eb',
    text: isDarkMode ? '#f1f5f9' : '#0f172a',
    muted: isDarkMode ? '#94a3b8' : '#64748b',
  };

  const status = product.status || 'pending';
  const isPending = status === 'pending' || status === 'draft';
  const money = (n) => `₦${Number(n || 0).toLocaleString()}`;

  const images = (product.images || [])
    .map(img => (typeof img === 'string' ? img : img?.url))
    .filter(Boolean);

  const categoryName =
    categories.find(cat => (cat._id || cat.id) === (product.category_id || product.category))?.name
    || (typeof product.category === 'string' && !/^[a-f0-9]{24}$/i.test(product.category) ? product.category : null)
    || 'Uncategorised';

  const colours = product.colors?.length ? product.colors.join(', ') : (product.color || '—');

  const specs = (() => {
    const raw = product.specifications;
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    if (raw.details && typeof raw.details === 'string') return raw.details;
    const pairs = Object.entries(raw).filter(([, v]) => v !== null && v !== undefined && v !== '');
    return pairs.length ? pairs.map(([k, v]) => `${k}: ${v}`).join('\n') : null;
  })();

  const Row = ({ label, value, accent }) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', gap: '1rem',
      padding: '0.55rem 0', borderBottom: `1px solid ${c.border}`,
    }}>
      <span style={{ color: c.muted, fontSize: '0.83rem', flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: '0.86rem', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word',
        color: accent || c.text,
      }}>
        {value}
      </span>
    </div>
  );

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: '1.3rem' }}>
      <h4 style={{
        margin: '0 0 0.5rem', fontSize: '0.74rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.07em', color: c.muted,
      }}>
        {title}
      </h4>
      {children}
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(3px)',
        zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto', overscrollBehavior: 'contain',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Review listing: ${product.title || 'Untitled'}`}
        style={{
          background: c.surface, color: c.text, borderRadius: '20px',
          width: '100%', maxWidth: '860px', maxHeight: 'min(calc(100dvh - 2rem), 900px)', margin: 'auto',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          border: `1px solid ${c.border}`, boxShadow: '0 26px 80px rgba(0,0,0,0.5)',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: '1.1rem 1.4rem', borderBottom: `1px solid ${c.border}`, flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem',
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Listing Review</h3>
              <span style={{
                padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.68rem', fontWeight: 800,
                textTransform: 'uppercase', letterSpacing: '0.5px',
                background: isPending ? 'rgba(245,158,11,0.18)' : 'rgba(34,197,94,0.18)',
                color: isPending ? '#f59e0b' : '#22C55E',
              }}>
                {status}
              </span>
            </div>
            <div style={{ fontSize: '0.8rem', color: c.muted, marginTop: '0.2rem' }}>
              Read-only — nothing here changes the listing
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.muted, fontSize: '1.1rem', flexShrink: 0 }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Body ── */}
        {/* minHeight:0 — without it this panel won't shrink below its content,
            pushing the Approve/Reject footer out of the dialog. */}
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0, padding: '1.3rem 1.4rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '1.5rem' }}>
            <div style={{ display: 'grid', gap: '1.5rem', gridTemplateColumns: images.length ? 'minmax(240px, 340px) minmax(0, 1fr)' : '1fr' }}>
              {/* Images */}
              {images.length > 0 && (
                <div>
                  <div style={{
                    borderRadius: '14px', overflow: 'hidden', background: c.raised,
                    border: `1px solid ${c.border}`, aspectRatio: '1 / 1',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <img
                      src={images[activeImage]}
                      alt={`${product.title || 'Product'} — image ${activeImage + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  {images.length > 1 && (
                    <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.55rem', flexWrap: 'wrap' }}>
                      {images.map((src, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveImage(i)}
                          aria-label={`Show image ${i + 1}`}
                          style={{
                            width: '52px', height: '52px', borderRadius: '9px', overflow: 'hidden',
                            padding: 0, cursor: 'pointer', background: c.raised,
                            border: `2px solid ${i === activeImage ? '#22C55E' : c.border}`,
                          }}
                        >
                          <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: '0.76rem', color: c.muted, marginTop: '0.5rem' }}>
                    <FontAwesomeIcon icon={faImage} /> {images.length} image{images.length > 1 ? 's' : ''}
                  </div>
                </div>
              )}

              {/* Key facts */}
              <div style={{ minWidth: 0 }}>
                <h2 style={{ margin: '0 0 0.3rem', fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.3 }}>
                  {product.title || 'Untitled Product'}
                </h2>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: '#22C55E', marginBottom: '1rem' }}>
                  {money(product.price)}
                </div>

                <Section title="Listing">
                  <Row label="Category" value={categoryName} />
                  <Row label="Brand" value={product.brand || '—'} />
                  <Row label="Model" value={product.model || '—'} />
                  <Row label="Colour(s)" value={colours} />
                  <Row label="Condition" value={(product.condition || '—').toString().replace(/_/g, ' ')} />
                  <Row label="Quantity" value={product.quantity ?? 0} />
                  {product.sku ? <Row label="SKU" value={product.sku} /> : null}
                  {product.seller_price ? <Row label="Wholesale price" value={money(product.seller_price)} /> : null}
                  <Row label="Swap accepted" value={product.swap_available ? 'Yes' : 'No'} />
                </Section>

                <Section title="Seller">
                  <Row label="Name" value={product.seller_name || 'Unknown'} />
                  {product.seller_rating ? <Row label="Rating" value={`${Number(product.seller_rating).toFixed(1)} ★`} /> : null}
                  <Row
                    label="Location"
                    value={[product.location?.city, product.location?.state, product.location?.country].filter(Boolean).join(', ') || '—'}
                  />
                  <Row label="Submitted" value={product.created_at ? new Date(product.created_at).toLocaleString() : '—'} />
                </Section>
              </div>
            </div>

            {/* Description — the field the admin most needs and the one that
                was only reachable through the Edit form. */}
            <Section title="Description">
              <div style={{
                padding: '0.9rem 1rem', borderRadius: '12px', background: c.raised,
                border: `1px solid ${c.border}`, fontSize: '0.88rem', lineHeight: 1.65,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {product.description || <span style={{ color: c.muted }}>No description provided.</span>}
              </div>
            </Section>

            {specs && (
              <Section title="Specifications">
                <div style={{
                  padding: '0.9rem 1rem', borderRadius: '12px', background: c.raised,
                  border: `1px solid ${c.border}`, fontSize: '0.85rem', lineHeight: 1.6,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}>
                  {specs}
                </div>
              </Section>
            )}

            {product.tags?.length > 0 && (
              <Section title="Tags">
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {product.tags.map(tag => (
                    <span key={tag} style={{
                      padding: '0.25rem 0.65rem', borderRadius: '20px', fontSize: '0.76rem',
                      background: c.raised, border: `1px solid ${c.border}`, color: c.muted,
                    }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </Section>
            )}
          </div>
        </div>

        {/* ── Actions ── */}
        <div style={{
          padding: '0.95rem 1.4rem', borderTop: `1px solid ${c.border}`, flexShrink: 0,
          display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap',
          background: c.surface,
        }}>
          {product.status === 'active' && (
            <a
              href={`/product/${product._id || product.id}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                padding: '0.65rem 1.1rem', borderRadius: '11px', textDecoration: 'none',
                border: `1px solid ${c.border}`, color: c.text, fontWeight: 600, fontSize: '0.86rem',
                marginRight: 'auto',
              }}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} /> View live page
            </a>
          )}
          <button
            onClick={onClose}
            style={{
              padding: '0.65rem 1.25rem', borderRadius: '11px', cursor: 'pointer',
              border: `1px solid ${c.border}`, background: 'transparent', color: c.text,
              fontWeight: 600, fontSize: '0.88rem',
            }}
          >
            Close
          </button>
          {isPending && (
            <>
              <button
                onClick={() => { onReject?.(product._id || product.id); onClose(); }}
                style={{
                  padding: '0.65rem 1.25rem', borderRadius: '11px', cursor: 'pointer', border: 'none',
                  background: 'linear-gradient(135deg,#EF4444,#DC2626)', color: '#fff',
                  fontWeight: 700, fontSize: '0.88rem',
                }}
              >
                <FontAwesomeIcon icon={faTimes} /> Reject
              </button>
              <button
                onClick={() => { onApprove?.(product._id || product.id); onClose(); }}
                style={{
                  padding: '0.65rem 1.25rem', borderRadius: '11px', cursor: 'pointer', border: 'none',
                  background: 'linear-gradient(135deg,#22C55E,#16A34A)', color: '#fff',
                  fontWeight: 700, fontSize: '0.88rem',
                  boxShadow: '0 6px 18px rgba(34,197,94,0.35)',
                }}
              >
                <FontAwesomeIcon icon={faCheckCircle} /> Approve
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductReviewModal;
